import React, { useContext, useState } from "react";

import classNames from "classnames";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import ConfirmModal from "../modal/ConfirmModal";

import type { JSX } from "react";

export default function FormTranslationsActions(): JSX.Element {
  const { t } = useTranslation("languages");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    fieldArray,
    activeLanguageIndex,
    setActiveLanguageIndex,
    onDeleteLanguage,
    methods,
    getSetAsDefaultDisabledReason,
  } = useContext(FormTranslationsContext);

  const currentLanguage = fieldArray?.fields[activeLanguageIndex]?.language;

  const activeTranslation = useWatch({
    control: methods?.control,
    name: `translations.${activeLanguageIndex}`,
    disabled: !getSetAsDefaultDisabledReason,
  });

  const setAsDefaultDisabledReason = activeTranslation
    ? getSetAsDefaultDisabledReason?.(activeTranslation)
    : undefined;

  const handleSetDefaultLanguage = (): void => {
    if (!currentLanguage) return;
    if (setAsDefaultDisabledReason) return;

    const translations = methods?.getValues("translations");

    if (!translations) return;

    const withNewDefault = translations.map((translation, index) =>
      index === activeLanguageIndex
        ? { ...translation, default: true }
        : { ...translation, default: false },
    );
    const currentItem = withNewDefault[activeLanguageIndex];

    if (!currentItem) return;

    const sorted = [
      currentItem,
      ...withNewDefault
        .filter((_, index) => index !== activeLanguageIndex)
        .sort((a, b) => a.language.localeCompare(b.language)),
    ];

    fieldArray.replace(sorted);
    setActiveLanguageIndex(0);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      const allValues = methods?.getValues("translations") ?? [];
      const removedTranslation = allValues[activeLanguageIndex];

      if (!removedTranslation) return;

      const values = allValues.filter((_, i) => i !== activeLanguageIndex);

      // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression
      await onDeleteLanguage?.(values, removedTranslation);

      fieldArray?.remove(activeLanguageIndex);

      const defaultLanguageIndex = values.findIndex((v) => v.default);

      setActiveLanguageIndex(
        defaultLanguageIndex !== -1 ? defaultLanguageIndex : 0,
      );

      // onDeleteLanguage saves the current form values (including pending edits) to
      // the server, so all remaining translations are now in their saved state.
      // Reset default values to current form values so RHF doesn't mark the
      // remaining translations as dirty due to array index shifting after remove().
      methods?.reset(methods.getValues(), { keepDirty: false });

      setIsModalOpen(false);
    } catch (error) {
      console.error("Error deleting language:", error);
    }
  };

  const handleDeleteLanguage = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <React.Fragment>
      <div className="my-[0.3125rem] flex flex-wrap items-center justify-start gap-[0.625rem]">
        <div className="text-muted-foreground text-[0.9375rem]">
          {t("contentIn")}{" "}
          <span className="font-semibold uppercase">
            {t(`langs.${currentLanguage}`)}
          </span>
        </div>
        {fieldArray?.fields[activeLanguageIndex]?.default ? (
          <span className="bg-muted text-muted-foreground rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-[0.78125rem] font-medium">
            {t("defaultLanguage")}
          </span>
        ) : (
          <div className="flex flex-wrap gap-[0.625rem]">
            <div
              data-tooltip-content={setAsDefaultDisabledReason}
              data-tooltip-id="app-tooltip"
              onClick={handleSetDefaultLanguage}
              className={classNames(
                "bg-primary/5 text-primary flex items-center rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-center text-[0.78125rem] font-medium transition-colors",
                setAsDefaultDisabledReason
                  ? "cursor-not-allowed opacity-50 grayscale-100"
                  : "hover:bg-primary/10 hover:text-primary/80 cursor-pointer",
              )}
            >
              {t("selectAsDefault")}
            </div>
            <div
              className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive/80 flex cursor-pointer items-center rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-center text-[0.78125rem] font-medium transition-colors"
              onClick={handleDeleteLanguage}
            >
              {t("deleteLanguage")}
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        onClose={(): void => setIsModalOpen(false)}
        title={t("modal.delete.title")}
        actions={[
          {
            label: t("modal.delete.confirm"),
            onClick: handleConfirmDelete,
            buttonVariant: "delete",
          },
          {
            label: t("modal.delete.cancel"),
            onClick: () => setIsModalOpen(false),
          },
        ]}
      >
        <p>
          {t("modal.delete.body", {
            languageName: t(
              `langs.${fieldArray?.fields[activeLanguageIndex]?.language}`,
            ),
          })}
        </p>
      </ConfirmModal>
    </React.Fragment>
  );
}
